import { scoreEntity } from '../opportunity_calculator/scoringEngine';
import { normalizeWeights } from '../opportunity_calculator/indicatorConfig';
import { toPoints } from '../opportunity_calculator/scoreScale';

// Overall risk score is on a 100-point scale (indicator scores 1-5 x 20).
export const DEFAULT_TIER_THRESHOLDS = { moderate: 40, high: 60, veryHigh: 80 };

export const RISK_TIERS = [
  { id: 'low', label: 'Low', color: '#2a9d2a' },
  { id: 'moderate', label: 'Moderate', color: '#e6a100' },
  { id: 'high', label: 'High', color: '#e0612b' },
  { id: 'veryHigh', label: 'Very High', color: '#b3141b' },
];

export const NO_SCORE_TIER = { id: 'none', label: 'Not scored', color: '#aaaaaa' };

export function tierFor(points, thresholds = DEFAULT_TIER_THRESHOLDS) {
  if (points === null || points === undefined || !Number.isFinite(Number(points))) return NO_SCORE_TIER;
  const value = Number(points);
  if (value >= thresholds.veryHigh) return RISK_TIERS[3];
  if (value >= thresholds.high) return RISK_TIERS[2];
  if (value >= thresholds.moderate) return RISK_TIERS[1];
  return RISK_TIERS[0];
}

/** Moderate or higher. */
export function isElevated(tier) {
  return tier?.id === 'moderate' || tier?.id === 'high' || tier?.id === 'veryHigh';
}

/**
 * Score every school and sort by risk (highest first).
 * Returns rows: { school, total, points, tier, contributions, coverage, rank }.
 */
export function scoreRiskSchools(schools, indicators, weights, options = {}) {
  const {
    thresholds = DEFAULT_TIER_THRESHOLDS,
    renormalize = true,
    autoNormalize = true,
    breakpointsById = {},
  } = options;
  const effectiveWeights = autoNormalize ? normalizeWeights(weights) : weights;
  return (schools || [])
    .map((school) => {
      const result = scoreEntity(school, indicators, effectiveWeights, { renormalize, breakpointsById });
      const points = result.coverage.scored ? toPoints(result.total) : null;
      return { school, ...result, points, tier: tierFor(points, thresholds) };
    })
    .sort((a, b) => {
      const pa = a.points ?? -1;
      const pb = b.points ?? -1;
      if (pb !== pa) return pb - pa;
      return String(a.school?.name || '').localeCompare(String(b.school?.name || ''));
    })
    .map((row, index) => ({ ...row, rank: index + 1 }));
}

/** The indicators pushing a school's risk up the most (by weighted points). */
export function topDrivers(row, count = 2) {
  return (row?.contributions || [])
    .filter((item) => !item.missing && item.score >= 3)
    .sort((a, b) => b.weighted - a.weighted)
    .slice(0, count);
}
